import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Eye, Search, ReceiptText, Loader2, AlertCircle, X
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SaleInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/sale-invoices`, getAuthHeaders());
      setInvoices(res.data);
    } catch (err) { setError('Failed to fetch sale invoices'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/sale-invoices/${id}`, getAuthHeaders());
      setDeleteConfirm(null); fetchInvoices();
    } catch (err) { setError('Failed to delete sale invoice'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const filtered = invoices.filter(i =>
    (i.invoice_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (i.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="sale-invoices-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Sale Invoices</h1>
          <p className="text-slate-600 mt-1">Manage sale invoices</p>
        </div>
        {perms.canAdd && (
        <button onClick={() => navigate('/admin/sale/invoices/new')} data-testid="create-invoice-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Create Sale Invoice
        </button>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by invoice number or customer..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <ReceiptText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No sale invoices yet</h3>
            <p className="text-slate-500 mb-6">Create your first sale invoice</p>
            <button onClick={() => navigate('/admin/sale/invoices/new')}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <Plus className="h-5 w-5" /> Create First Invoice
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-9 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Invoice No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Customer</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Type</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Total</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Received</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Balance</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((inv) => (
              <div key={inv.id} data-testid={`invoice-row-${inv.id}`}
                className="grid grid-cols-1 md:grid-cols-9 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="text-slate-600 text-sm">{formatDate(inv.invoice_date || inv.created_at)}</div>
                <div className="col-span-2 font-mono font-medium text-slate-900">{inv.invoice_number || '-'}</div>
                <div className="text-slate-700 text-sm">{inv.customer_name || '-'}</div>
                <div>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    inv.sale_type === 'Credit' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                  }`}>{inv.sale_type}</span>
                </div>
                <div className="font-medium text-slate-900">₹{inv.total?.toLocaleString()}</div>
                <div className="text-green-600 font-medium">₹{(inv.received_amount || 0).toLocaleString()}</div>
                <div className={`font-medium ${(inv.balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{(inv.balance || 0).toLocaleString()}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => navigate(`/admin/sale/invoices/${inv.id}`)} data-testid={`view-btn-${inv.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                    <Eye className="h-4 w-4" />
                  </button>
                  {perms.canDelete && (
                  <button onClick={() => setDeleteConfirm(inv.id)} data-testid={`delete-btn-${inv.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this sale invoice?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
